import { RefObject } from 'react';
import type Konva from 'konva';
import type { KonvaEventObject } from 'konva/lib/Node';
import { LayoutObject as LayoutObjectType } from '../../../../types/types';
import { LayoutFixedCell } from '../../../../utils/layouts/getLayoutFixedCell';
import { snapSizeToGrid } from '../../../../utils/layouts/snapSizeToGrid';
import { snapToGrid } from '../../../../utils/layouts/snapToGrid';
import { LAYOUT_OBJECT_MIN_SIZE } from '../../../../constants';
import { clampRectToCanvas } from '../../../../utils/layouts/clampRectToCanvas';

interface LayoutObjectHandlersParams {
  index: number;
  // Effective geometry (fixed cell already applied by the component).
  x: number;
  y: number;
  width: number;
  height: number;
  snapEnabled: boolean;
  gridX: number;
  gridY: number;
  gapX: number;
  gapY: number;
  // Fixed mode: the grid cell replaces the object geometry (index-derived).
  fixedCell?: LayoutFixedCell;
  groupRef: RefObject<Konva.Group>;
  canvasWidth: number;
  canvasHeight: number;
  onSelect: (index: number) => void;
  onChange: (index: number, patch: Partial<LayoutObjectType>) => void;
  onReorderDrop?: (index: number, centerX: number, centerY: number) => void;
}

// Select/drag/transform glue of a canvas object — the geometry math lives
// here, LayoutObject keeps only the Konva markup.
export const useLayoutObjectHandlers = ({
  index,
  x,
  y,
  width,
  height,
  snapEnabled,
  gridX,
  gridY,
  gapX,
  gapY,
  fixedCell,
  groupRef,
  canvasWidth,
  canvasHeight,
  onSelect,
  onChange,
  onReorderDrop,
}: LayoutObjectHandlersParams) => {
  const handleSelect = (): void => {
    onSelect(index);
  };

  // The dragged object must render above the others (Konva z-order = render
  // order); the next React render restores the array order again.
  const handleDragStart = (event: KonvaEventObject<DragEvent>): void => {
    event.target.moveToTop();
  };

  const handleDragEnd = (event: KonvaEventObject<DragEvent>): void => {
    const rawX = Math.round(event.target.x());
    const rawY = Math.round(event.target.y());

    // Fixed grid: the drop reorders the list — the node snaps back to its
    // index-derived cell (the position never lives on the object itself).
    if (fixedCell) {
      event.target.position({ x: fixedCell.x, y: fixedCell.y });
      onReorderDrop?.(index, rawX + width / 2, rawY + height / 2);
      return;
    }

    const nextX = snapEnabled ? snapToGrid(rawX, gridX, gapX) : rawX;
    const nextY = snapEnabled ? snapToGrid(rawY, gridY, gapY) : rawY;

    // No-op drags (a plain click) must not write — they would materialize
    // fallback values (missing x/y → 0) and mark the object as dirty.
    if (nextX === x && nextY === y) {
      return;
    }

    onChange(index, { x: nextX, y: nextY });
  };

  const handleTransformEnd = (): void => {
    const node = groupRef.current;

    if (!node) {
      return;
    }

    const scaleX = node.scaleX();
    const scaleY = node.scaleY();

    node.scaleX(1);
    node.scaleY(1);

    const rawX = Math.round(node.x());
    const rawY = Math.round(node.y());
    const rawWidth = Math.max(
      LAYOUT_OBJECT_MIN_SIZE,
      Math.round(width * scaleX)
    );
    const rawHeight = Math.max(
      LAYOUT_OBJECT_MIN_SIZE,
      Math.round(height * scaleY)
    );

    const snappedX = snapEnabled ? snapToGrid(rawX, gridX, gapX) : rawX;
    const snappedY = snapEnabled ? snapToGrid(rawY, gridY, gapY) : rawY;
    // Sizes snap to whole grid cells (spanning the gaps between them).
    const snappedWidth = snapEnabled
      ? snapSizeToGrid(rawWidth, gridX, gapX)
      : rawWidth;
    const snappedHeight = snapEnabled
      ? snapSizeToGrid(rawHeight, gridY, gapY)
      : rawHeight;

    // The gesture itself is unbounded (see resolveTransformerBox) — the final
    // geometry is pulled back into the canvas here.
    const next = clampRectToCanvas(
      {
        x: snappedX,
        y: snappedY,
        width: snappedWidth,
        height: snappedHeight,
      },
      canvasWidth,
      canvasHeight
    );

    // The node may physically sit outside the canvas after the gesture — an
    // unchanged x/y prop is not re-applied by react-konva, so the position
    // must be restored imperatively.
    node.position({ x: next.x, y: next.y });

    // No-op transforms must not write — they would materialize fallback values
    // (missing geometry → 0) and mark the object as dirty.
    if (
      next.x === x &&
      next.y === y &&
      next.width === width &&
      next.height === height
    ) {
      return;
    }

    onChange(index, {
      x: next.x,
      y: next.y,
      width: next.width,
      height: next.height,
    });
  };

  return { handleSelect, handleDragStart, handleDragEnd, handleTransformEnd };
};
